/**
 * v0.9.0 §1.1 — plane presence over the board's module tree.
 *
 * Pure over the scene `Module[]` (each carrying `planes` from the wire), so the
 * plane switcher can list the planes in play and, for a chosen plane, resolve
 * the set of node ids present on it — the input to a board focus/dim.
 *
 * The **primary** plane is implicit (every module) and never enumerated here;
 * selecting it means "no plane filter" (show the whole structural model).
 */

import type { Module } from "@archlang/scene/arch-model";

export interface PlaneInfo {
  /** Every non-primary plane in the model, sorted. */
  readonly planes: readonly string[];
  /** Plane name → the node ids present on it (a module is identified by the same
   *  `id` the board uses — its `#id` or qualified-name fallback). */
  readonly idsByPlane: ReadonlyMap<string, ReadonlySet<string>>;
}

/** A module's board id — its minted `#id`, else the qualified path (matching the
 *  wire serializer's `moduleDiffId` fallback the board selects on). */
function boardId(m: Module, parentPath: readonly string[]): string {
  return m.id ?? [...parentPath, m.name].join(".");
}

export function computePlaneInfo(roots: readonly Module[]): PlaneInfo {
  const planes = new Set<string>();
  const idsByPlane = new Map<string, Set<string>>();
  const visit = (m: Module, parentPath: readonly string[]): void => {
    const id = boardId(m, parentPath);
    for (const p of m.planes ?? []) {
      planes.add(p);
      const set = idsByPlane.get(p) ?? new Set<string>();
      set.add(id);
      idsByPlane.set(p, set);
    }
    for (const c of m.children) visit(c, [...parentPath, m.name]);
  };
  for (const m of roots) visit(m, []);
  return { planes: [...planes].sort(), idsByPlane };
}

/** The node ids to FOCUS for a selected plane (empty/undefined → no filter,
 *  i.e. the primary/whole-model view). */
export function planeMemberIds(info: PlaneInfo, plane: string | null): readonly string[] {
  if (!plane) return [];
  return [...(info.idsByPlane.get(plane) ?? [])];
}
